import React, { memo, useMemo } from 'react'
import GridLayout from 'react-grid-layout'
import { Icon, Intent } from '@blueprintjs/core'
import Immutable from 'immutable'

import { IconName, IconNames } from '@blueprintjs/icons'
import CX from 'classnames'
import uniq from 'lodash.uniq'
import clonedeep from 'lodash.clonedeep'
import compiler from './Engine'
import { FormRendererSwitcher } from './renderers'
import { UprightStyle } from '../../../../types'
import { SomeImmutableJsThing } from './types'
import { stringToBool } from '../../../../utils/string'

const LAYOUT_DEFAULTS = Immutable.Map({
  minW: 2,
  maxW: 6,
  maxH: 4
})

const styles: UprightStyle = {
  duplicateInput: { marginLeft: 25, cursor: 'pointer' }
}

interface Props extends BaseProps {
  layout: unknown[]
  form: unknown[]
  container: Record<string, unknown>
  // Handle certain combobox changes
  onInputChange: (index, input: Record<string, unknown>) => void
  requiredLabelIconName?: IconName
}

interface PropsImmutableJS extends BaseProps {
  layout: SomeImmutableJsThing
  form: SomeImmutableJsThing
  container: SomeImmutableJsThing
  // Handle certain combobox changes
  onInputChange: (index, input: SomeImmutableJsThing) => void
}

interface BaseProps {
  previewMode: boolean
  onConfigureInput: (index) => void | undefined
  // Handle when most form inputs change
  onFormPreviewChange: (index, value) => void

  onLayoutChange: (newLayout) => void | undefined
  onDuplicateInputClick: (index) => void | undefined
  templateId?: number
}

// For newer code that doesn't want/need to work with ImmutableJS
export const Renderer = memo((props: Props) => {
  const { layout, form, container, onInputChange, ...neverImmutableProps } = props

  const form2 = form.map((e: any) => {
    return { ...e, props: { ...e.props, requiredLabelIconName: props.requiredLabelIconName || null } }
  })

  return (
    <RendererImmutableJS
      layout={Immutable.fromJS(layout)}
      form={Immutable.fromJS(form2)}
      container={Immutable.fromJS(container)}
      onInputChange={(index, input: SomeImmutableJsThing) => {
        // keep the interface for Renderer free of ImmutableJS
        // Test this by creating a Builder Template with a combobox, then typing a custom value into it, then choosing another value.
        onInputChange(index, input?.toJS())
      }}
      {...neverImmutableProps}
    />
  )
})

function getSortOrder(combined) {
  // Keys are item.i. Values are a number that specifies sort order
  const sortOrderInner = {}

  let combinedInner = clonedeep(combined) // clonedeep helps prove to the linter that we're not causing bugs by sorting this

  // const variableNames = ['measurements']
  const allVariableNames = combinedInner.map(c => c.formItem.get('variableName')).filter(String) // filter(String) - filter out nulls

  // Prune variableNames so it only includes the variables that are referenced by other fields.
  // Some complex templates (see GW Horizon) have ~30 variables, so otherwise our inner loop could easily run 30^2 times
  const allTheHiddenExpressions = uniq(
    combinedInner
      .map(c => c.formItem.get('props').get('hidden')) // please eslint
      .filter(String)
  ).join('')
  const variableNames = allVariableNames.filter(variableName => allTheHiddenExpressions.includes(variableName))

  /* eslint-disable no-param-reassign */
  variableNames.forEach(variableName => {
    // Mutate combined, set a "sortKey" field.
    // Fields that depend on (i.e. are hidden based on) measurements will get values like 6.01, 6.02, 6.03, etc.
    // Other fields (including the measurements field itself) will get values like 0, 1, 2, 3, 4, 5, 6, 7, etc.

    combinedInner.forEach(c => {
      c.isDependent = c.formItem.get('props').get('hidden').includes(variableName)

      // In cases where a hidden field references two variables, this still works.
      // Whichever variable was processed last will win, but
      // the exact behavior isn't important: the field will get pulled immediately after _some_ field it depends on.
      // See test case "some fields are hidden based on 2 variables".
    })

    // the sortKey of the field which has the variable name "measurements".
    // For example, 6
    const sortKeyOfVariableName = combinedInner
      .filter(c => !c.isDependent)
      .findIndex(c => c.formItem.get('variableName') === variableName)

    let otherFieldIndex = 0
    let dependentFieldIndex = 0.01
    combinedInner.forEach(c => {
      if (c.isDependent) {
        c.sortKey = sortKeyOfVariableName + dependentFieldIndex
        dependentFieldIndex += 0.01
      } else {
        c.sortKey = otherFieldIndex
        otherFieldIndex += 1
      }
    })

    combinedInner = combinedInner.sort((a, b) => a.sortKey - b.sortKey)
  })

  combinedInner.forEach((c, i) => {
    sortOrderInner[c.item.i] = i
  })

  return sortOrderInner
}

// Legacy version that works with ImmutableJS props
const RendererImmutableJS = (props: PropsImmutableJS) => {
  const { previewMode, form, onDuplicateInputClick } = props
  const container = props.container.toJS()

  const layout = props.layout.map(l => LAYOUT_DEFAULTS.merge(l)).toJS()

  let combined = layout.map((item, index) => {
    const formItem = form.get(index)

    // a combined object that has both form and layout.
    // so that we can sort an array by simply accessing nested fields in the next step
    return {
      item,
      formItem,
      index
    }
  })

  // Rearrange the order for single column mode (express list),
  // so that we try to honor their intent and keep related fields together.
  const sortOrder = useMemo(() => {
    // It's hard to memoize "form" or "combined", b/c it contains the form field values.
    // But we can memoize the sort order, since that never changes during listing.

    if (container.cols !== 1) return {}

    return getSortOrder(combined)

    // We are making _some_ assumptions suppressing the linter here. But ExpressList will
    // ensure that a new templateId is provided any time the template changes (and this needs to be recalculated).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.templateId, container.cols])

  if (container.cols === 1) {
    // This sort (during render) is much faster than computing the sort order
    combined = combined.sort((a, b) => sortOrder[a.item.i] - sortOrder[b.item.i])
  }

  const children = combined.map((c, mapIndex) => {
    const { item, formItem, index } = c

    return (
      <div
        className="ui-grid-box"
        key={item.i}
        style={{
          zIndex: form.getIn([index, 'type']) === 'combobox' ? layout.length - mapIndex : 0,
          display:
            previewMode && stringToBool(compiler(formItem.getIn(['props', 'hidden']), form, false, false))
              ? 'none'
              : 'block'
        }}
      >
        <FormRendererSwitcher
          input={formItem}
          index={index}
          onFormPreviewChange={props.onFormPreviewChange}
          onInputChange={props.onInputChange}
          previewMode={previewMode}
        />
        {!previewMode && <div className="ui-blocker" />}
        {!previewMode && (
          <Icon icon={IconNames.COG} onClick={() => props.onConfigureInput(index)} className="ui-configure-btn" />
        )}
        {!previewMode && (
          <Icon
            icon={IconNames.DUPLICATE}
            onClick={() => onDuplicateInputClick(index)}
            className="ui-configure-btn"
            style={styles.duplicateInput}
          />
        )}
        {!previewMode && formItem.getIn(['props', 'hidden']) && (
          <Icon intent={Intent.WARNING} icon={IconNames.EYE_OFF} className="ui-hidden-indicator" />
        )}
      </div>
    )
  })

  if (container.cols === 1) {
    return <div className={CX('product-attributes-container', previewMode ? '' : 'ui-layout')}>{children}</div>
  }

  return (
    <div className={previewMode ? '' : 'ui-layout'}>
      <GridLayout
        onLayoutChange={previewMode ? () => {} : props.onLayoutChange}
        className="layout"
        compactType={null}
        preventCollision
        layout={layout}
        cols={container.cols}
        rowHeight={container.rowHeight}
        width={container.width}
        isDraggable={!previewMode}
        isResizable={!previewMode}
      >
        {children}
      </GridLayout>
    </div>
  )
}

export default RendererImmutableJS
